/**
 * The upload transport seam: the sink the scrub pipeline pushes scrubbed CSV
 * parts into.
 *
 * The scrub pipeline (parse → drop PII → HMAC IDs → emit CSV → chunk) is
 * sink-agnostic; only the transport differs. {@link UploadTransport} is the seam,
 * so a future presigned-PUT-to-R2 transport drops in behind it without touching
 * the pipeline (IMPLEMENTATION_PLAN Conflict F: MVP ships the Worker multipart
 * relay; presign is a later swap-in).
 *
 * {@link WorkerRelayTransport} is the MVP implementation. It calls the transfer
 * contracts: `transferInit` opens a Worker→R2 multipart session, each
 * `uploadPart` streams one part body straight into the Worker (raw
 * `application/octet-stream`, bypassing `apiClient` which is JSON-only — the same
 * carve-out the files demo documents), `complete` finalizes the multipart, and
 * `abort` tears it down on failure/cancel.
 *
 * Browser-only: uses `fetch`, `Blob`, and the WebCrypto-independent contracts.
 */

import {
  transferInitContract,
  transferCompleteContract,
  transferAbortContract,
  transferPartContract,
  TransferPartResponseSchema,
} from '../../common/contracts/uploads';
import type { UploadPartRef } from '../../common/contracts/uploads';
import type { FileType, UploadFormat } from '../../common/uploader';
import { apiClient } from '../api-client';
import { AppError, ValidationError, ExternalServiceError } from '../../common/errors';

/** Metadata the transport needs to open a session. */
export interface UploadBeginMeta {
  /**
   * The shared upload-EVENT id (minted once per session via `POST /api/uploads`).
   * Threaded into transfer-init so this file attaches to the session's event.
   */
  readonly uploadId: string;
  readonly fileType: FileType;
  readonly format: UploadFormat;
  /** Best-effort declared row count (for the session row); not authoritative. */
  readonly declaredRows: number;
  /**
   * The SCRUBBED-output column name designated at preflight as the Loan ID (or null
   * when none). Sent at transfer-init so the upload-time ingest pins each row's
   * protected Loan ID from this column. The Loan/Tax ID columns are HMAC'd IN PLACE
   * and keep their original header name in the scrubbed output, so this is that name
   * — matching what the server parses from the scrubbed header.
   */
  readonly loanIdColumn?: string | null;
  /** The scrubbed-output column name designated at preflight as the Tax ID (or null). */
  readonly taxIdColumn?: string | null;
  /**
   * The designated columns' POSITIONS in the scrubbed output header (F1 index
   * seam) — the authoritative identity designation the ingest pins by (the names
   * above ride along as a defense-in-depth equality assertion).
   */
  readonly loanIdColumnIndex?: number | null;
  readonly taxIdColumnIndex?: number | null;
}

/** Result of a completed upload — the scrubbed object's location + size. */
export interface UploadCompleteResult {
  readonly uploadId: string;
  readonly r2Key: string;
  readonly bytes: number;
}

/**
 * The sink seam. Lifecycle: `begin` once → `uploadPart` per part (1-indexed,
 * ascending, all but the last ≥ the R2 5 MB minimum) → `complete` once. `abort`
 * tears the session down on any failure or user cancel.
 */
export interface UploadTransport {
  /** Open a session. Must be called before any `uploadPart`. */
  begin(meta: UploadBeginMeta): Promise<void>;
  /** Upload one part; returns nothing — the transport tracks the part refs. */
  uploadPart(bytes: Blob): Promise<void>;
  /**
   * Finalize the upload. Resolves with the scrubbed object's location + size.
   * `fileName` is the source file's original name, carried through so the
   * persisted record shows the real filename (display metadata only).
   * `dataQuality` ships the scrub pass's accumulated parse-structure signals
   * (P5 — ragged rows / parse errors); optional (absent on legacy callers).
   */
  complete(
    fileName: string,
    dataQuality?: { readonly raggedRowCount: number; readonly parseErrorCount: number },
  ): Promise<UploadCompleteResult>;
  /** Abort and clean up the session. Safe to call before `begin` (no-op). */
  abort(): Promise<void>;
}

/**
 * Retry policy for a single part upload. Exponential backoff, capped. A big
 * upload (500k rows → dozens of parts over many minutes) must survive a transient
 * blip — a dropped socket on sleep/wake, a 5xx, a rate-limit — without aborting
 * the whole multipart session and forcing a full re-upload.
 */
export interface PartRetryPolicy {
  /** Total attempts including the first (so 6 = 1 try + 5 retries). */
  readonly maxAttempts: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
}

/**
 * Default: 6 attempts, 0.5s→8s exponential backoff (~15s of total patience). Wide
 * enough to ride out a sleep/wake reconnection or a brief network drop; short
 * enough that a genuinely dead upload surfaces its error (and the per-file Retry)
 * without a long hang.
 */
export const DEFAULT_PART_RETRY_POLICY: PartRetryPolicy = {
  maxAttempts: 6,
  baseDelayMs: 500,
  maxDelayMs: 8_000,
};

/** Transient HTTP statuses worth retrying: request-timeout, rate-limit, and 5xx. */
function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

/** A user/unmount abort — never retried, always surfaced as a cancel. */
function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * Classify a part-upload failure as transient (retry) vs terminal (surface now).
 * Network + body-read failures (`ExternalServiceError`) and transient HTTP statuses
 * retry; a contract mismatch (`ValidationError`) or a 4xx (bad request, auth, a
 * `409 transfer already completed`) is terminal — retrying can't fix it.
 */
function isRetryablePartError(err: unknown): boolean {
  if (err instanceof ValidationError) return false;
  if (err instanceof ExternalServiceError) return true;
  if (err instanceof AppError) return isRetryableStatus(err.statusCode);
  return false;
}

/**
 * Worker multipart relay transport. Bytes transit the Worker but are never
 * buffered there — each part streams straight into `r2.uploadPart` — so the
 * Worker is a pass-through pipe, not a buffer (IMPLEMENTATION_PLAN Risk #1).
 *
 * Each part is uploaded with bounded exponential-backoff retry (see
 * {@link PartRetryPolicy}), so a transient failure re-PUTs the same part number
 * (idempotent on R2 — it overwrites) rather than aborting the whole session.
 * `parts` / `nextPartNumber` advance only on success, so a retry always reuses the
 * same number and pushes exactly one ref. An optional `signal` cancels an in-flight
 * part and short-circuits the backoff wait, so a user/unmount abort tears down
 * promptly instead of waiting out a retry.
 */
export class WorkerRelayTransport implements UploadTransport {
  /** THIS file's transfer-session id (the wire `transferId`), set by `begin`. */
  private transferId: string | null = null;
  private nextPartNumber = 1;
  private readonly parts: UploadPartRef[] = [];
  private readonly signal: AbortSignal | undefined;
  private readonly retryPolicy: PartRetryPolicy;

  constructor(signal?: AbortSignal, retryPolicy: PartRetryPolicy = DEFAULT_PART_RETRY_POLICY) {
    this.signal = signal;
    this.retryPolicy = retryPolicy;
  }

  async begin(meta: UploadBeginMeta): Promise<void> {
    // `meta.uploadId` is the shared EVENT id (the wire field is `eventId`); the
    // returned `transfer.transferId` is THIS file's transfer-session id (what every
    // later part/complete/abort addresses). They are distinct — don't conflate.
    const { transfer } = await apiClient(transferInitContract, {
      body: {
        eventId: meta.uploadId,
        fileType: meta.fileType,
        format: meta.format,
        declaredRows: meta.declaredRows,
        loanIdColumn: meta.loanIdColumn ?? null,
        taxIdColumn: meta.taxIdColumn ?? null,
        loanIdColumnIndex: meta.loanIdColumnIndex ?? null,
        taxIdColumnIndex: meta.taxIdColumnIndex ?? null,
      },
    });
    this.transferId = transfer.transferId;
  }

  async uploadPart(bytes: Blob): Promise<void> {
    if (this.transferId === null) {
      throw new AppError('uploadPart called before begin()', { code: 'TRANSPORT_STATE' });
    }
    const partNumber = this.nextPartNumber;
    // The part body is a raw binary stream, not JSON — `apiClient` only sends
    // JSON bodies, so this one request stays on raw `fetch` (the same carve-out
    // the files demo documents). The path is built from the typed contract so the
    // route still lines up; the JSON response is validated against the contract.
    const path = transferPartContract.path
      .replace(':transferId', encodeURIComponent(this.transferId))
      .replace(':partNumber', String(partNumber));

    // Retry the part on transient failures — advancing `parts`/`nextPartNumber`
    // only after a success, so a retry reuses the same part number (idempotent on
    // R2) and pushes exactly one ref.
    const part = await this.uploadPartWithRetry(path, bytes);
    this.parts.push(part);
    this.nextPartNumber += 1;
  }

  /** One PUT of a part: stream the body to R2 and validate the etag response. */
  private async uploadPartOnce(path: string, bytes: Blob): Promise<UploadPartRef> {
    let response: Response;
    try {
      response = await fetch(path, {
        method: transferPartContract.method,
        headers: { 'content-type': 'application/octet-stream' },
        body: bytes,
        ...(this.signal !== undefined ? { signal: this.signal } : {}),
      });
    } catch (err) {
      if (isAbortError(err)) throw err; // an aborted fetch is a cancel, not a failure
      throw new ExternalServiceError('Upload part network request failed', { cause: err });
    }
    if (!response.ok) {
      throw new AppError(`Upload part failed (HTTP ${response.status})`, {
        code: 'HTTP_ERROR',
        statusCode: response.status,
      });
    }
    let json: unknown;
    try {
      json = await response.json();
    } catch (err) {
      // A truncated body on a flaky connection reads as a transient failure.
      throw new ExternalServiceError('Upload part response read failed', { cause: err });
    }
    const result = TransferPartResponseSchema.safeParse(json);
    if (!result.success) {
      throw new ValidationError('Upload part response validation failed', {
        cause: result.error,
      });
    }
    return result.data.part;
  }

  /** Upload one part with bounded exponential-backoff retry on transient errors. */
  private async uploadPartWithRetry(path: string, bytes: Blob): Promise<UploadPartRef> {
    const { maxAttempts } = this.retryPolicy;
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      this.throwIfAborted();
      try {
        return await this.uploadPartOnce(path, bytes);
      } catch (err) {
        if (isAbortError(err)) throw err; // never retry a cancel
        if (!isRetryablePartError(err)) throw err; // terminal — surface immediately
        lastError = err;
        if (attempt >= maxAttempts) break;
        await this.backoffBeforeRetry(attempt);
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new ExternalServiceError('Upload part failed after retries');
  }

  private throwIfAborted(): void {
    if (this.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
  }

  /**
   * Wait out the exponential backoff before the next attempt, settling early
   * (rejecting AbortError) if the signal fires mid-wait so a cancel never has to
   * wait out the delay.
   */
  private backoffBeforeRetry(attempt: number): Promise<void> {
    const { baseDelayMs, maxDelayMs } = this.retryPolicy;
    const delay = Math.min(baseDelayMs * 2 ** (attempt - 1), maxDelayMs);
    const signal = this.signal;
    return new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new DOMException('Upload aborted', 'AbortError'));
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, delay);
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  async complete(
    fileName: string,
    dataQuality?: { readonly raggedRowCount: number; readonly parseErrorCount: number },
  ): Promise<UploadCompleteResult> {
    if (this.transferId === null) {
      throw new AppError('complete called before begin()', { code: 'TRANSPORT_STATE' });
    }
    // Transfer-complete returns the persisted FILE (addressed downstream by its
    // `uploadFileId`); the result's `uploadId` carries that file id.
    const { file } = await apiClient(transferCompleteContract, {
      params: { transferId: this.transferId },
      body: {
        parts: this.parts,
        fileName,
        ...(dataQuality !== undefined ? { dataQuality } : {}),
      },
    });
    return { uploadId: file.uploadFileId, r2Key: file.r2Key, bytes: file.bytes };
  }

  async abort(): Promise<void> {
    if (this.transferId === null) return; // never began — nothing to clean up.
    await apiClient(transferAbortContract, { params: { transferId: this.transferId } });
  }
}
